import { useGetPage } from 'hooks/useGetPage';
import { genericActionsIds, pageIds } from 'utilities/constants';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getFirebaseAuth } from '../../../../../../firebase/config';
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  MultiFactorError,
  MultiFactorResolver,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { useFirebaseSmsMfa } from '../../../../../../firebase/hooks';
import { FIREBASE_ERROR_CODES } from '../../../../../../firebase/interfaces';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import { useNavigate } from 'react-router-dom';
import { PasswordChangeVerification } from './PasswordChangeVerification';
import { PasswordChangeForm } from './PasswordChangeForm';
import Modal from 'components/modal/modalComponent';
import useValidationYup from 'hooks/useValidationYup';
import { changePasswordValidationSchema } from 'utilities/validationSchemas/password';
import { codeSchema } from 'utilities/validationSchemas/mfaSchemas';
import { useTimer } from 'hooks/useTimer';
import { SECONDS_IN_MIN } from 'utilities/constants/time';
import { useUpdateFirebase } from 'hooks/authProvider/useUpdateFirebase';
import Alert from '../../../../../../components/alertComponent';
import { useHandleFirebaseError } from '../../../../hooks/useHandleFirebaseError';
import { useCustomToast } from '../../../../../../layout/useToast';

export type ChangePasswordForm = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

interface PasswordChangeProps {
  handleUpdatePassword: () => void;
}

export const PasswordChange: React.FC<PasswordChangeProps> = ({
  handleUpdatePassword: handleUpdatePasswordCallback,
}) => {
  const { data: firebaseLocale, loading: firebaseLocaleLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.FIREBASE_ERRORS,
  });

  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [
        genericActionsIds.CANCEL,
        genericActionsIds.CLOSE,
        genericActionsIds.SUBMIT,
        genericActionsIds.VERIFY,
        genericActionsIds.UPDATE,
      ],
    });

  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MY_PROFILE,
  });

  const [displayMFAForm, setDisplayMFAForm] = useState<boolean>(false);
  useState<boolean>(false);
  const [passwordChangeError, setPasswordChangeError] = useState<string>('');
  const [verificationId, setVerificationId] = useState<string>('');
  const [mfaError, setMfaError] = useState<string>('');
  const [code, setCode] = useState<string>('');

  const recaptchaContainerWrapper = useRef<HTMLDivElement>(null);
  const resolver = useRef<MultiFactorResolver>();

  const { handleFirebaseError } = useHandleFirebaseError();
  const { addToast } = useCustomToast();

  const { handleVerifyPhoneNumberResolver, handleResolveSignIn } =
    useFirebaseSmsMfa(firebaseLocale, (error: unknown) => {
      if (error instanceof FirebaseError) {
        handleFirebaseError(error.code)
          .then((message) =>
            addToast({
              message: message as string,
              type: 'warning',
            }),
          )
          .catch(console.warn);
      }
    });
  const firebaseAuth = getFirebaseAuth();

  const [formData, setFormData] = useState<ChangePasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const navigate = useNavigate();

  const handleCancel = useCallback(() => navigate(-1), [navigate]);

  const codeValue = useMemo(() => ({ code }), [code]);

  const { errors, isValid } = useValidationYup(
    changePasswordValidationSchema,
    formData,
    locale?.securityAndPrivacy,
  );

  const {
    errors: codeError,
    setShowError: setShowCodeError,
    isValid: isCodeValid,
  } = useValidationYup(codeSchema, codeValue, locale?.securityAndPrivacy);

  const {
    timeLeft,
    temporalyDisableResendActions,
    setTimeLeft,
    setTemporalyDisableResendActions,
  } = useTimer();

  const handleResendCode = () => {
    setTemporalyDisableResendActions(true);
    setTimeLeft(SECONDS_IN_MIN);
    onResendCode();
  };

  const handleOnCodeChange = (code: string) => {
    setMfaError('');
    setCode(code);
  };

  const handleOnInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setPasswordChangeError('');
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { updateFirebaseUserPassword } = useUpdateFirebase();

  const resolveMFA = useCallback(
    async (resolver: MultiFactorResolver) => {
      if (recaptchaContainerWrapper.current) {
        try {
          const verificationId = await handleVerifyPhoneNumberResolver(
            resolver,
            'recaptcha-container',
          );
          setVerificationId(verificationId);
          setDisplayMFAForm(true);
        } catch (error) {
          setPasswordChangeError(locale.securityAndPrivacy.verifyPhoneError);
        }
      }
    },
    [handleVerifyPhoneNumberResolver, locale?.securityAndPrivacy],
  );

  const onResendCode = async () => {
    if (!resolver.current) return;
    resolveMFA(resolver.current);
  };

  const handleUpdatePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      recentlyAuthenticated = false,
    ) => {
      const currentUser = firebaseAuth.currentUser;
      if (currentUser?.email) {
        try {
          if (!recentlyAuthenticated) {
            const credential = EmailAuthProvider.credential(
              currentUser.email,
              currentPassword,
            );

            await reauthenticateWithCredential(currentUser, credential);
          }
          const token = await currentUser.getIdToken(true);
          await updateFirebaseUserPassword({
            newPassword,
            firebaseToken: token,
          });

          navigate(-1);
          handleUpdatePasswordCallback();
        } catch (error) {
          if (error instanceof FirebaseError) {
            switch (error.code) {
              case FIREBASE_ERROR_CODES.AUTH_MULTI_FACTOR_AUTH_REQUIRED:
                resolver.current = getMultiFactorResolver(
                  firebaseAuth,
                  error as MultiFactorError,
                );
                resolveMFA(resolver.current);
                break;
              case FIREBASE_ERROR_CODES.AUTH_WRONG_PASSWORD:
                setPasswordChangeError(locale.securityAndPrivacy.wrongPassword);
                break;
              case FIREBASE_ERROR_CODES.AUTH_TOO_MANY_REQUESTS:
                setPasswordChangeError(
                  locale.securityAndPrivacy.tooManyRequests,
                );
                break;
              default:
                console.error(error);
                break;
            }
          } else {
            console.error(error);
          }
        }
      }
    },
    [
      firebaseAuth,
      updateFirebaseUserPassword,
      navigate,
      handleUpdatePasswordCallback,
      resolveMFA,
      locale,
    ],
  );

  const handleVerifyCode = useCallback(async () => {
    if (!isCodeValid) {
      setShowCodeError(true);
      return;
    }

    if (verificationId && resolver.current) {
      try {
        await handleResolveSignIn(resolver.current, code, verificationId);
        await handleUpdatePassword(
          formData.currentPassword,
          formData.newPassword,
          true,
        );
      } catch (error) {
        setMfaError(locale.securityAndPrivacy.invalidVerificationCode);
      }
    }
  }, [
    isCodeValid,
    verificationId,
    setShowCodeError,
    handleResolveSignIn,
    code,
    handleUpdatePassword,
    formData.currentPassword,
    formData.newPassword,
    locale?.securityAndPrivacy?.invalidVerificationCode,
  ]);

  const handleSubmit = useCallback(async () => {
    if (isValid) {
      await handleUpdatePassword(
        formData.currentPassword,
        formData.newPassword,
      );
    }
  }, [formData, handleUpdatePassword, isValid]);

  const buttons = useMemo(
    () => [
      {
        label: displayMFAForm
          ? genericAction?.[genericActionsIds.UPDATE].update
          : genericAction?.[genericActionsIds.SUBMIT].submit,
        onClick: displayMFAForm ? handleVerifyCode : handleSubmit,
        disabled: !isValid,
        testID: displayMFAForm ? 'update' : 'submit',
      },
      {
        label: genericAction?.[genericActionsIds.CANCEL].cancel,
        type: 'underline',
        onClick: handleCancel,
        testID: 'cancel',
      },
    ],
    [
      displayMFAForm,
      genericAction,
      handleVerifyCode,
      handleSubmit,
      isValid,
      handleCancel,
    ],
  );

  useEffect(() => {
    if (displayMFAForm) {
      setTemporalyDisableResendActions(true);
      setTimeLeft(SECONDS_IN_MIN);
    }
  }, [displayMFAForm, setTemporalyDisableResendActions, setTimeLeft]);

  useEffect(() => {
    setShowCodeError(false);
  }, [setShowCodeError]);

  if (
    (loading && !locale) ||
    (firebaseLocaleLoading && !firebaseLocale) ||
    (genericActionLoading && !genericAction)
  )
    return null;

  return (
    <Modal
      buttons={buttons}
      contentPaddingY="py-30"
      modalDesktopWidth="desktop:max-w-[600px]"
      isOpen={true}
      title={locale.securityAndPrivacy.changePassword}
      contentContainerGap="gap-0"
      onClose={handleCancel}
      disableMobileGrow
    >
      <Alert
        type="info"
        text="Once your password is changed, your current session will expire. For security reasons, you will need to log in again"
      />
      <div className="my-5">
        {displayMFAForm ? (
          <PasswordChangeVerification
            mfaError={mfaError}
            onCodeChange={handleOnCodeChange}
            disableResendActions={temporalyDisableResendActions}
            timer={timeLeft}
            handleResendCode={handleResendCode}
            code={code}
            codeError={codeError?.code}
            codeBlur={() => setShowCodeError(true)}
          />
        ) : (
          <PasswordChangeForm
            errors={errors}
            passwordChangeError={passwordChangeError}
            onChange={handleOnInputChange}
          />
        )}
      </div>
      <div ref={recaptchaContainerWrapper}>
        <div id="recaptcha-container" />
      </div>
    </Modal>
  );
};
